import type { Delivery } from '@igus/iglidur-free-form-hub-interfaces';
import { DeliveryTimeLevel } from '@igus/kopla-domain';
import { useTranslation } from 'next-i18next';
import { ProgressIndicator, ProgressShape } from '@icommerce/ui';

type DeliveryInfoProps = {
  delivery?: Delivery;
  isLoading?: boolean;
};

export const DeliveryInfo = ({ delivery, isLoading = false }: DeliveryInfoProps) => {
  const { t } = useTranslation(['category/productFilter', 'common']);

  if (isLoading) {
    return (
      <ProgressIndicator
        label={t(`COMMON.LOADING`, { ns: 'free-form-hub/freeFormHubCommon' })}
        shape={ProgressShape.Circular}
        size={28}
        dataTestId="delivery-loading-spinner"
        className="block mx-auto my-2"
      />
    );
  }

  if (!delivery) {
    return null;
  }

  const colorMap: { [key in DeliveryTimeLevel]: string } = {
    [DeliveryTimeLevel.fastlane]: 'text-availability-fastlane',
    [DeliveryTimeLevel.fastAvailability]: 'text-availability-fast',
    [DeliveryTimeLevel.slowAvailability]: 'text-availability-slow',
    [DeliveryTimeLevel.onRequest]: 'text-availability-on-request',
  };

  const textColor = colorMap[delivery.level];

  const deliveryTime = () => {
    if (delivery.level === DeliveryTimeLevel.onRequest) {
      return t(`COMMON.UPON_REQUEST`, { ns: 'free-form-hub/freeFormHubCommon' });
    }
    const showRange = delivery.min !== delivery.max;
    const formattedType = delivery?.type?.toUpperCase();
    return showRange
      ? `${delivery.min}-${delivery.max} ${t(`COMMON.${formattedType}`, { ns: 'free-form-hub/freeFormHubCommon' })}`
      : `${delivery.min} ${t(`COMMON.${formattedType}`, { ns: 'free-form-hub/freeFormHubCommon' })}`;
  };

  return <span className={`${textColor} font-bold`}>{deliveryTime()}</span>;
};
